import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  CardHeader,
  CardContent,
  Chip,
  Typography,
  Tooltip,
  CircularProgress,
  Alert,
  Divider,
  Drawer,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  InputAdornment,
} from "@mui/material";
import GlassCard from "../Cards/GlassCard";
import CloseIcon from "@mui/icons-material/Close";
import { DataGrid } from "@mui/x-data-grid";
import SortRoundedIcon from "@mui/icons-material/SortRounded";
import SearchIcon from "@mui/icons-material/Search";
import axiosInstance from "../../services/axiosInstance";
import DrillDownPanel from "./DrillDownPanel";
import {
  SUCCESS, SUCCESS_BG,
  WARNING, WARNING_BG,
  DANGER,  DANGER_BG,
  PLAGIARISM_HIGH_HEX, PLAGIARISM_MEDIUM_HEX, PLAGIARISM_LOW_HEX,
  TABLE_BORDER_LIGHT as heatmapPaperBorder,
  TABLE_BORDER_AMBIENT,
  dataGridAmbientBaseSx,
} from "../../utils/colors";

// ── Score → visual style ──────────────────────────────────────────────────────

/**
 * Returns the border / text colour for a cell based on its score.
 *   >= 80  →  Green
 *   60–79  →  Light Green
 *   40–59  →  Yellow / Warning
 *   <  40  →  Red / Danger
 *   null   →  Gray (not attempted)
 */
const getCellColor = (score) => {
  if (score === null || score === undefined) return "#9E9E9E";
  if (score >= 80) return SUCCESS;
  if (score >= 60) return "#52B788";
  if (score >= 40) return WARNING;
  return DANGER;
};

/** Lighter fill that pairs with getCellColor for the cell background. */
const getCellBg = (score) => {
  if (score === null || score === undefined) return "#F5F5F5";
  if (score >= 80) return SUCCESS_BG;
  if (score >= 60) return "#D8F3DC";
  if (score >= 40) return WARNING_BG;
  return DANGER_BG;
};

// ── Plagiarism label / color maps (mirrors DrillDownPanel) ───────────────────
const PLAG_LABEL = { clean: "Clean", suspected: "Suspected", highSimilarity: "High Similarity" };
const PLAG_COLOR = { clean: PLAGIARISM_LOW_HEX, suspected: PLAGIARISM_MEDIUM_HEX, highSimilarity: PLAGIARISM_HIGH_HEX };

// ── Legend items ───────────────────────────────────────────────────────────────
const LEGEND = [
  { label: "Excellent", range: "80\u2013100", color: SUCCESS,    bg: SUCCESS_BG  },
  { label: "Good",      range: "60\u201379",  color: "#52B788", bg: "#D8F3DC"   },
  { label: "Average",   range: "40\u201359",  color: WARNING,    bg: WARNING_BG  },
  { label: "Poor",      range: "< 40",   color: DANGER,     bg: DANGER_BG   },
  { label: "Not Attempted", range: null, color: "#9E9E9E", bg: "#F5F5F5"   },
];

// ── Component ──────────────────────────────────────────────────────────────────

/**
 * StudentProgressHeatmap
 *
 * Renders a grid where every row is a student and every column is an
 * assignment.  Each cell shows the student's score (0–100) or "—" if the
 * assignment has not been attempted.
 *
 * @param {string} batchId  – MongoDB ObjectId of the batch to load
 */
const StudentProgressHeatmap = ({ batchId }) => {
  const [heatmapData, setHeatmapData] = useState(null);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);

  // ── Cell-click drill-down state ────────────────────────────────────────────
  const [cellDetail, setCellDetail]           = useState(null);  // { summary, rows, title }
  const [cellDetailLoading, setCellDetailLoading] = useState(false);
  const [cellDetailError, setCellDetailError] = useState(null);


  // ── Sort state ───────────────────────────────────────────────────────────────
  const [sortType, setSortType] = useState("totalScore");

  // ── Search state ─────────────────────────────────────────────────────────
  const [filterText, setFilterText] = useState("");

  // ── Heatmap fetch ──────────────────────────────────────────────────────────
  const fetchHeatmap = useCallback(async () => {
    if (!batchId) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await axiosInstance.get(
        `/analytics/batch/${batchId}/heatmap`
      );
      setHeatmapData(data);
    } catch (err) {
      setError(
        err.response?.data?.message || "Failed to load heatmap data."
      );
    } finally {
      setLoading(false);
    }
  }, [batchId]);

  useEffect(() => {
    fetchHeatmap();
  }, [fetchHeatmap]);

  // ── Cell-click handler ─────────────────────────────────────────────────────
  const handleCellClick = useCallback(async (assignmentId, assignmentTitle, studentId, studentName) => {
    // Clicking the same cell again closes the panel
    if (cellDetail?.assignmentId === assignmentId && cellDetail?.studentId === studentId) {
      setCellDetail(null);
      return;
    }
    setCellDetail(null);
    setCellDetailError(null);
    setCellDetailLoading(true);
    try {
      const { data } = await axiosInstance.get(
        `/analytics/assignment/${assignmentId}/student/${studentId}`
      );
      const rows = data.problems.map((p) => ({
        id:               p.slug,
        name:             p.title,
        link:             p.link,
        difficulty:       p.difficulty,
        solved:           p.solved,
        solvedAt:         p.solvedAt,
        language:         p.language,
        plagiarismStatus: p.plagiarismStatus,
      }));
      setCellDetail({
        assignmentId,
        studentId,
        title:  `${assignmentTitle} · ${studentName}`,
        summary: {
          name:             data.name,
          rollNumber:       data.rollNumber,
          assignment:       data.assignment?.title,
          score:            data.score,
          plagiarismStatus: data.plagiarismStatus,
          lastSubmission:   data.lastSubmission,
          leetcodeUsername: heatmapData.students.find((s) => s.studentId === studentId)?.leetcodeUsername || null,
        },
        rows,
      });
    } catch (err) {
      setCellDetailError(
        err.response?.data?.message || "Failed to load submission details."
      );
    } finally {
      setCellDetailLoading(false);
    }
  }, [cellDetail, heatmapData]);

  // ── Derived data ───────────────────────────────────────────────────────────
  const assignments = useMemo(() => heatmapData?.assignments ?? [], [heatmapData]);
  const students    = useMemo(() => heatmapData?.students    ?? [], [heatmapData]);
  // ── Sorted students ───────────────────────────────────────────────────────────
  const sortedStudents = useMemo(() => {
    const copy = [...students];
    if (sortType === "name") {
      copy.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    } else {
      // Helper: sum all non-null scores for a student
      const totalScore = (s) =>
        Object.values(s.scores).reduce((acc, v) => acc + (v ?? 0), 0);
      // Helper: average over attempted assignments only
      const avgScore = (s) => {
        const vals = Object.values(s.scores).filter((v) => v !== null);
        return vals.length === 0 ? 0 : vals.reduce((a, v) => a + v, 0) / vals.length;
      };
      copy.sort((a, b) =>
        sortType === "totalScore"
          ? totalScore(b) - totalScore(a)
          : avgScore(b)  - avgScore(a)
      );
    }
    return copy;
  }, [students, sortType]);

  // ── Filtered students (sits between sortedStudents and dgRows) ────────────
  const filteredStudents = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    if (!q) return sortedStudents;
    return sortedStudents.filter((s) => {
      const nameMatch = s.name?.toLowerCase().includes(q) ?? false;
      const rollMatch = s.rollNumber?.toLowerCase().includes(q) ?? false;
      return nameMatch || rollMatch;
    });
  }, [sortedStudents, filterText]);

  // ── DataGrid rows & columns ────────────────────────────────────────────────
  const dgRows = useMemo(
    () =>
      filteredStudents.map((s) => ({
        id:    s.studentId,
        _name: s.name,
        _roll: s.rollNumber,
        ...s.scores,
      })),
    [filteredStudents]
  );

  const dgColumns = useMemo(() => {
    if (!assignments.length) return [];
    return [
      {
        field:      "_name",
        headerName: "Student",
        width:      200,
        sortable:   false,
        renderCell: (params) => (
          <Box sx={{ overflow: "hidden", py: 0.5 }}>
            <Typography variant="body2" fontWeight={600} noWrap>
              {params.row._name}
            </Typography>
            {params.row._roll && (
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                sx={{ display: "block", fontSize: "0.65rem" }}
              >
                {params.row._roll}
              </Typography>
            )}
          </Box>
        ),
      },
      ...assignments.map((a) => ({
        field:       a.id,
        headerName:  a.title,
        width:       52,
        sortable:    false,
        align:       "center",
        headerAlign: "center",
        // eslint-disable-next-line react/display-name
        renderHeader: () => (
          <Tooltip key={a.id} title={a.title} placement="top" arrow>
            <Box
              sx={{
                width:          44,
                height:         72,
                display:        "flex",
                alignItems:     "flex-end",
                justifyContent: "center",
                pb:             0.75,
                overflow:       "hidden",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  writingMode:  "vertical-rl",
                  transform:    "rotate(180deg)",
                  fontWeight:   600,
                  color:        "text.secondary",
                  fontSize:     "0.65rem",
                  maxHeight:    64,
                  overflow:     "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace:   "nowrap",
                }}
              >
                {a.title}
              </Typography>
            </Box>
          </Tooltip>
        ),
        // eslint-disable-next-line react/display-name
        renderCell: (params) => {
          const score    = params.value ?? null;
          const color    = getCellColor(score);
          const bg       = getCellBg(score);
          const isActive =
            cellDetail?.assignmentId === a.id &&
            cellDetail?.studentId    === params.row.id;
          return (
            <Box
              sx={{
                display:        "flex",
                width:          "100%",
                height:         "100%",
                alignItems:     "center",
                justifyContent: "center",
              }}
            >
              <Tooltip
                arrow
                placement="top"
                title={
                  <Box sx={{ textAlign: "center", py: 0.25 }}>
                    <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.4 }}>
                      {params.row._name}
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ opacity: 0.85, mb: 0.5 }}>
                      {a.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ color: score === null ? "#aaa" : "#fff" }}
                    >
                      {score === null ? "Not Attempted" : `${score}%`}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      Click to view details
                    </Typography>
                  </Box>
                }
              >
                <Box
                  onClick={() =>
                    handleCellClick(a.id, a.title, params.row.id, params.row._name)
                  }
                  sx={{
                    width:          40,
                    height:         40,
                    bgcolor:        bg,
                    border:         `2px solid ${color}`,
                    borderRadius:   1,
                    display:        "flex",
                    alignItems:     "center",
                    justifyContent: "center",
                    fontSize:       11,
                    fontWeight:     score !== null ? 700 : 500,
                    color,
                    cursor:         "pointer",
                    userSelect:     "none",
                    transition:     "transform 0.12s ease, box-shadow 0.12s ease",
                    ...(isActive
                      ? { outline: `3px solid ${color}`, outlineOffset: 2 }
                      : {}),
                    "&:hover": {
                      transform: "scale(1.18)",
                      boxShadow: `0 0 0 2px ${color}55`,
                      zIndex:    10,
                      position:  "relative",
                    },
                  }}
                >
                  {score ?? "—"}
                </Box>
              </Tooltip>
            </Box>
          );
        },
      })),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignments, cellDetail, handleCellClick]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
    <GlassCard hoverable={false} sx={{ borderRadius: 4 }}>
      <CardHeader
        title={
          <Typography variant="h6" fontWeight={700}>
            Student Progress Heatmap
          </Typography>
        }
        subheader={
          !loading && heatmapData && (
            <Typography variant="body2" color="text.secondary">
              {students.length} student{students.length !== 1 ? "s" : ""}
              {" · "}
              {assignments.length} assignment{assignments.length !== 1 ? "s" : ""}
            </Typography>
          )
        }
        sx={{ px: 2.5, pt: 2.5, pb: 0 }}
      />

      <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
        {/* ── Loading ────────────────────────────────────────────────────── */}
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={40} />
          </Box>
        )}

        {/* ── Error ─────────────────────────────────────────────────────── */}
        {!loading && error && (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {/* ── Empty state ────────────────────────────────────────────────── */}
        {!loading && !error && heatmapData && (
          assignments.length === 0 || students.length === 0 ? (
            <Box sx={{ py: 8, textAlign: "center" }}>
              <Typography color="text.secondary">
                No data available for this batch.
              </Typography>
            </Box>
          ) : (
            <>              {/* ── Sort controls ─────────────────────────────────────────────────── */}
              <Box
                sx={{
                  display:    "flex",
                  alignItems: "center",
                  gap:        1.5,
                  mb:         2,
                  flexWrap:   "wrap",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <SortRoundedIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                  <Typography variant="caption" fontWeight={600} color="text.secondary"
                    sx={{ textTransform: "uppercase", letterSpacing: "0.06em" }}
                  >
                    Sort by
                  </Typography>
                </Box>
                <ToggleButtonGroup
                  value={sortType}
                  exclusive
                  onChange={(_e, val) => { if (val) setSortType(val); }}
                  size="small"
                  sx={{
                    "& .MuiToggleButton-root": {
                      px: 1.5, py: 0.4,
                      fontSize: "0.75rem",
                      textTransform: "none",
                      fontWeight: 600,
                    },
                  }}
                >
                  <ToggleButton value="totalScore">Total Score</ToggleButton>
                  <ToggleButton value="avgScore">Avg Score</ToggleButton>
                  <ToggleButton value="name">Name (A–Z)</ToggleButton>
                </ToggleButtonGroup>

                {/* ── Search input ──────────────────────────────────────── */}
                <TextField
                  size="small"
                  placeholder="Search by name or roll number"
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    ml:      "auto",
                    minWidth: 240,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      fontSize:     "0.82rem",
                    },
                  }}
                />
              </Box>
              {/* ── Legend ──────────────────────────────────────────────── */}
              <Box
                sx={{
                  display:      "flex",
                  flexWrap:     "wrap",
                  gap:          1.5,
                  mb:           2.5,
                  alignItems:   "center",
                  pb:           2,
                  borderBottom: "1px solid",
                  borderColor:  "divider",
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight={600}
                  color="text.secondary"
                  sx={{ mr: 0.5, textTransform: "uppercase", letterSpacing: "0.06em" }}
                >
                  Legend
                </Typography>
                {LEGEND.map(({ label, range, color, bg }) => (
                  <Box
                    key={label}
                    sx={{
                      display:      "flex",
                      alignItems:   "center",
                      gap:          0.75,
                      px:           1.25,
                      py:           0.5,
                      borderRadius: 2,
                      bgcolor:      bg,
                      border:       `1.5px solid ${color}33`,
                    }}
                  >
                    <Box
                      sx={{
                        width:        12,
                        height:       12,
                        borderRadius: 0.5,
                        bgcolor:      color,
                        flexShrink:   0,
                      }}
                    />
                    <Typography variant="caption" fontWeight={700} sx={{ color }}>
                      {label}
                    </Typography>
                    {range && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 0.25 }}>
                        ({range})
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>

              {/* ── Heatmap grid (MUI DataGrid) ──────────────────────────── */}
              {filteredStudents.length === 0 && filterText.trim() !== "" ? (
                <Box
                  sx={{
                    display:        "flex",
                    flexDirection:  "column",
                    alignItems:     "center",
                    justifyContent: "center",
                    py:             8,
                    border:         (theme) =>
                      `1px solid ${
                        theme.palette.mode === "dark" ? TABLE_BORDER_AMBIENT : heatmapPaperBorder
                      }`,
                    borderRadius:   1,
                    bgcolor:        "transparent",
                  }}
                >
                  <SearchIcon sx={{ fontSize: 36, color: "text.disabled", mb: 1.5 }} />
                  <Typography variant="body2" fontWeight={600} color="text.secondary">
                    No students found for this search.
                  </Typography>
                  <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5 }}>
                    Try a different name or roll number.
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    border: (theme) =>
                      `1px solid ${
                        theme.palette.mode === "dark" ? TABLE_BORDER_AMBIENT : heatmapPaperBorder
                      }`,
                    borderRadius: 1,
                    overflow: "hidden",
                  }}
                >
                  <DataGrid
                    rows={dgRows}
                    columns={dgColumns}
                    rowHeight={52}
                    columnHeaderHeight={80}
                    disableRowSelectionOnClick
                    hideFooter
                    disableColumnMenu
                    sx={[
                      dataGridAmbientBaseSx,
                      {
                        height: 500,
                        // Remove default focus outlines — we manage our own
                        "& .MuiDataGrid-cell:focus": { outline: "none" },
                        "& .MuiDataGrid-cell:focus-within": { outline: "none" },
                        "& .MuiDataGrid-columnHeader:focus": { outline: "none" },
                        "& .MuiDataGrid-columnHeader:focus-within": { outline: "none" },
                        "& .MuiDataGrid-columnSeparator": { display: "none" },
                        "& .MuiDataGrid-cell": {
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: 0,
                        },
                        "& .MuiDataGrid-cell[data-field='_name']": {
                          justifyContent: "flex-start",
                          pl: 1,
                        },
                        "& .MuiDataGrid-columnHeader": { padding: 0 },
                      },
                    ]}
                  />
                </Box>
              )}
            </>
          )
        )}
      </CardContent>
    </GlassCard>

      {/* ── Cell drill-down drawer ─────────────────────────────────────────────── */}
      <Drawer
        anchor="right"
        open={Boolean(cellDetailLoading || cellDetailError || cellDetail)}
        onClose={() => { setCellDetail(null); setCellDetailError(null); }}
        PaperProps={{
          sx: {
            width:   "80%",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* Drawer header */}
        <Box
          sx={{
            display:      "flex",
            alignItems:   "center",
            justifyContent: "space-between",
            px:           2.5,
            py:           1.75,
            borderBottom: "1px solid",
            borderColor:  "divider",
            flexShrink:   0,
          }}
        >
          <Typography variant="subtitle1" fontWeight={700}>
            {cellDetail?.title ?? "Submission Details"}
          </Typography>
          <IconButton
            size="small"
            onClick={() => { setCellDetail(null); setCellDetailError(null); }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Drawer body */}
        <Box
          sx={{
            flex:      1,
            overflowY: "auto",
            p:         2.5,
            // Thin styled scrollbar
            "&::-webkit-scrollbar":       { width: 6 },
            "&::-webkit-scrollbar-track": { borderRadius: 3, bgcolor: "grey.100" },
            "&::-webkit-scrollbar-thumb": { borderRadius: 3, bgcolor: "grey.400" },
          }}
        >
          {/* Loading spinner */}
          {cellDetailLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress size={36} />
            </Box>
          )}

          {/* Fetch error */}
          {!cellDetailLoading && cellDetailError && (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {cellDetailError}
            </Alert>
          )}

          {/* Detail panels */}
          {!cellDetailLoading && !cellDetailError && cellDetail && (
            <>
              {/* Summary strip: Student · Assignment · Score · Plagiarism · Last Submission */}
              <GlassCard
                hoverable={false}
                sx={{ borderRadius: 3, mb: 2 }}
              >
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5, alignItems: "center" }}>

                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">Student</Typography>
                      <Typography variant="body2" fontWeight={700}>
                        {cellDetail.summary.name}
                        {cellDetail.summary.rollNumber && (
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                            ({cellDetail.summary.rollNumber})
                          </Typography>
                        )}
                      </Typography>
                    </Box>

                    <Divider orientation="vertical" flexItem />

                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">Assignment</Typography>
                      <Typography variant="body2" fontWeight={600}>{cellDetail.summary.assignment}</Typography>
                    </Box>

                    <Divider orientation="vertical" flexItem />

                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">Score</Typography>
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        sx={{
                          color:
                            cellDetail.summary.score >= 80 ? SUCCESS :
                            cellDetail.summary.score >= 60 ? "#52B788" :
                            cellDetail.summary.score >= 40 ? WARNING : DANGER,
                        }}
                      >
                        {cellDetail.summary.score != null ? `${cellDetail.summary.score}%` : "—"}
                      </Typography>
                    </Box>

                    <Divider orientation="vertical" flexItem />

                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">Plagiarism</Typography>
                      {cellDetail.summary.plagiarismStatus ? (
                        <Chip
                          label={
                            PLAG_LABEL[cellDetail.summary.plagiarismStatus] ||
                            cellDetail.summary.plagiarismStatus
                          }
                          size="small"
                          sx={{
                            bgcolor: `${PLAG_COLOR[cellDetail.summary.plagiarismStatus] || "#999"}1a`,
                            color:    PLAG_COLOR[cellDetail.summary.plagiarismStatus] || "#999",
                            fontWeight: 600,
                            fontSize: "0.72rem",
                            height: 22,
                            border: `1px solid ${(PLAG_COLOR[cellDetail.summary.plagiarismStatus] || "#999")}33`,
                          }}
                        />
                      ) : (
                        <Typography variant="body2" color="text.disabled">—</Typography>
                      )}
                    </Box>

                    <Divider orientation="vertical" flexItem />

                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">Last Submission</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {cellDetail.summary.lastSubmission
                          ? new Date(cellDetail.summary.lastSubmission).toLocaleDateString(undefined, {
                              year: "numeric", month: "short", day: "numeric",
                            })
                          : "—"}
                      </Typography>
                    </Box>

                  </Box>
                </CardContent>
              </GlassCard>

              {/* Per-problem DrillDownPanel */}
              <DrillDownPanel
                title={cellDetail.title}
                data={cellDetail.rows}
                type="student-detail"
                studentId={cellDetail.studentId}
                leetcodeUsername={cellDetail.summary.leetcodeUsername}
                studentName={cellDetail.summary.name}
                batchId={batchId}
                onClose={() => { setCellDetail(null); setCellDetailError(null); }}
                cellDetail={cellDetail}
              />
            </>
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default StudentProgressHeatmap;
